import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Calendar,
  MapPin,
  Loader2,
  ImageOff,
  Package,
  X,
  ChevronLeft,
  ChevronRight,
  Camera,
  Download,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PhotoGalleryModal from '../components/PhotoGalleryModal';
import { apiUrl } from '../lib/api';
import type { PhotoResult, PackInfo, DownloadLink } from '../types';

/* ------------------------------------------------------------------ */
/*  Auth form (inline sign-up / login for surfers)                     */
/* ------------------------------------------------------------------ */

function InlineAuthForm({ onClose }: { onClose: () => void }) {
  const { login } = useAuth();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'signup' ? apiUrl('/api/auth/signup') : apiUrl('/api/auth/login');
      const body =
        mode === 'signup'
          ? { name, email, password, role: 'SURFER' }
          : { email, password };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Auth failed');
      login(data.user, data.token);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 relative mx-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-bold mb-6">
          {mode === 'login' ? 'Log in to purchase' : 'Create an account'}
        </h2>
        {error && (
          <p className="text-red-600 text-sm mb-4 bg-red-50 p-2 rounded">{error}</p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-lg"
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 border rounded-lg"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2 border rounded-lg"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Sign Up'}
          </button>
        </form>
        <p className="text-sm text-center mt-4 text-slate-500">
          {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
          <button
            onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
            className="text-blue-600 font-medium hover:underline"
          >
            {mode === 'login' ? 'Sign up' : 'Log in'}
          </button>
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Download modal                                                     */
/* ------------------------------------------------------------------ */

function DownloadModal({
  links,
  onClose,
}: {
  links: DownloadLink[];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8 relative max-h-[80vh] overflow-y-auto mx-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-bold mb-2">Purchase Complete</h2>
        <p className="text-slate-500 text-sm mb-6">
          Your unwatermarked, full-resolution files are ready. Links expire in 1 hour.
        </p>
        <div className="space-y-3">
          {links.map(link => (
            <a
              key={link.photoId}
              href={link.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors"
            >
              <Download className="h-5 w-5 text-blue-600 shrink-0" />
              <span className="truncate text-sm font-medium">{link.filename}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function SurferSearch() {
  // ---- Browse state ----
  const [photos, setPhotos] = useState<PhotoResult[]>([]);
  const [packs, setPacks] = useState<PackInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ---- Filter state ----
  const [beach, setBeach] = useState('');
  const [date, setDate] = useState('');
  const [wetsuitColor, setWetsuitColor] = useState('');
  const [boardType, setBoardType] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ---- Modal state ----
  const [galleryPack, setGalleryPack] = useState<PackInfo | null>(null);
  const [galleryPhotos, setGalleryPhotos] = useState<PhotoResult[] | null>(null);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState<DownloadLink[] | null>(null);

  // ---- Fetch data ----
  const fetchData = useCallback(
    async (pageNum: number) => {
      setLoading(true);
      const params = new URLSearchParams();
      if (beach.trim()) params.set('beach', beach.trim());
      if (date) params.set('date', date);
      if (wetsuitColor) params.set('wetsuitColor', wetsuitColor);
      if (boardType) params.set('boardType', boardType);
      params.set('page', String(pageNum));

      try {
        const res = await fetch(apiUrl(`/api/photos/browse?${params.toString()}`));
        const data = await res.json();
        setPhotos(data.photos ?? []);
        setPacks(data.packs ?? []);
        setTotalPages(data.totalPages ?? 1);
      } catch {
        setPhotos([]);
        setPacks([]);
      } finally {
        setLoading(false);
      }
    },
    [beach, date, wetsuitColor, boardType]
  );

  // Initial load
  useEffect(() => {
    fetchData(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setPage(1);
    fetchData(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setBeach('');
    setDate('');
    setWetsuitColor('');
    setBoardType('');
    setPage(1);
    // Re-fetch with no filters
    setLoading(true);
    fetch(apiUrl('/api/photos/browse?page=1'))
      .then(r => r.json())
      .then(data => {
        setPhotos(data.photos ?? []);
        setPacks(data.packs ?? []);
        setTotalPages(data.totalPages ?? 1);
      })
      .catch(() => { setPhotos([]); setPacks([]); })
      .finally(() => setLoading(false));
  };

  const hasActiveFilters = beach || date || wetsuitColor || boardType;

  // ---- Open gallery for a pack ----
  const openPack = (pack: PackInfo) => {
    setGalleryPack(pack);
    setGalleryPhotos(null);
    setGalleryStartIndex(0);
  };

  // ---- Open gallery for individual photos ----
  const openPhoto = (index: number) => {
    setGalleryPack(null);
    setGalleryPhotos(photos);
    setGalleryStartIndex(index);
  };

  const closeGallery = () => {
    setGalleryPack(null);
    setGalleryPhotos(null);
  };

  const isGalleryOpen = galleryPack !== null || galleryPhotos !== null;

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <>
      {/* Modals */}
      {showAuthForm && <InlineAuthForm onClose={() => setShowAuthForm(false)} />}
      {downloadLinks && (
        <DownloadModal links={downloadLinks} onClose={() => setDownloadLinks(null)} />
      )}
      {isGalleryOpen && (
        <PhotoGalleryModal
          pack={galleryPack}
          photos={galleryPhotos ?? undefined}
          startIndex={galleryStartIndex}
          onClose={closeGallery}
          onRequireAuth={() => setShowAuthForm(true)}
          onPurchaseComplete={links => {
            setDownloadLinks(links);
            closeGallery();
          }}
        />
      )}

      <div className="space-y-8">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
              Find Photos
            </h1>
            <p className="text-slate-500 mt-1">
              Browse surf photos and session packs from local photographers
            </p>
          </div>

          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              hasActiveFilters
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Search className="h-4 w-4" />
            {hasActiveFilters ? 'Filters Active' : 'Filter & Search'}
          </button>
        </div>

        {/* Collapsible filter bar */}
        {filtersOpen && (
          <form
            onSubmit={handleSearch}
            className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Beach
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="e.g. Pipeline"
                    value={beach}
                    onChange={e => setBeach(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Wetsuit Color
                </label>
                <select
                  value={wetsuitColor}
                  onChange={e => setWetsuitColor(e.target.value)}
                  className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                >
                  <option value="">Any</option>
                  <option value="Black">Black</option>
                  <option value="Blue">Blue</option>
                  <option value="Red">Red</option>
                  <option value="White">White</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Board Type
                </label>
                <select
                  value={boardType}
                  onChange={e => setBoardType(e.target.value)}
                  className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                >
                  <option value="">Any</option>
                  <option value="Shortboard">Shortboard</option>
                  <option value="Longboard">Longboard</option>
                  <option value="Fish">Fish</option>
                  <option value="Funboard">Funboard</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <button
                type="submit"
                className="px-5 py-2 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors text-sm flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </form>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-20 text-slate-400">
            <Loader2 className="h-10 w-10 mx-auto mb-4 animate-spin opacity-60" />
            <p className="text-lg">Loading photos...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && packs.length === 0 && photos.length === 0 && (
          <div className="text-center py-20 text-slate-400">
            <ImageOff className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No photos available yet</p>
            <p className="text-sm mt-1">
              {hasActiveFilters
                ? 'Try broadening your search filters'
                : 'Check back soon - photographers are uploading new sessions daily!'}
            </p>
          </div>
        )}

        {/* Session Packs */}
        {!loading && packs.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />
              Session Packs
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {packs.map(pack => (
                <button
                  key={pack._id}
                  onClick={() => openPack(pack)}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all text-left"
                >
                  {/* Cover image */}
                  <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden">
                    {pack.coverUrl ? (
                      <img
                        src={pack.coverUrl}
                        alt={pack.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="h-10 w-10 text-slate-300" />
                      </div>
                    )}
                    {/* Photo count badge */}
                    <span className="absolute top-3 left-3 bg-black/60 text-white text-xs font-bold px-2.5 py-1 rounded-md backdrop-blur-sm">
                      {pack.mediaCount} photos
                    </span>
                    {/* Pack price badge */}
                    <span className="absolute top-3 right-3 bg-white/90 text-slate-900 text-sm font-bold px-2.5 py-1 rounded-md shadow-sm">
                      ${pack.price.toFixed(2)}
                    </span>
                  </div>

                  {/* Pack info */}
                  <div className="p-4">
                    <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors truncate">
                      {pack.name}
                    </h3>
                    <div className="flex items-center gap-3 mt-1.5 text-sm text-slate-500">
                      {pack.beach && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {pack.beach}
                        </span>
                      )}
                      {pack.date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(pack.date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Individual Photos */}
        {!loading && photos.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Camera className="h-5 w-5 text-blue-600" />
              Individual Photos
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo, idx) => (
                <button
                  key={photo._id}
                  onClick={() => openPhoto(idx)}
                  className="group relative rounded-xl overflow-hidden bg-slate-100 text-left hover:shadow-lg transition-all"
                >
                  <div className="aspect-square relative overflow-hidden">
                    {photo.mediaType === 'image' ? (
                      <img
                        src={photo.watermarkedUrl}
                        alt={photo.filename}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <>
                        <video
                          src={photo.watermarkedUrl}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                        />
                        <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded font-medium">
                          Video
                        </span>
                      </>
                    )}
                    {/* Price overlay */}
                    <span className="absolute bottom-2 right-2 bg-white/90 text-slate-900 text-sm font-bold px-2 py-0.5 rounded shadow-sm">
                      ${photo.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs text-slate-500 truncate">
                      {photo.beach} &middot;{' '}
                      {new Date(photo.date).toLocaleDateString()}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-slate-600 px-3">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
